/**
 * FloorPlanUpload Component
 *
 * Allows gym owners to upload, replace, and delete floor plan images.
 * Includes confirmation dialogs for destructive operations that affect regions.
 */

import { useState, useRef, useEffect, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { ImageDropZone } from '@/components/common/ImageDropZone';
import { GymMapView } from '@/components/gym-map/GymMapView';
import {
  uploadFloorPlanImage,
  deleteFloorPlanImage,
  validateFloorPlanImage,
  FLOOR_PLAN_MAX_SIZE,
  FLOOR_PLAN_ALLOWED_TYPES,
} from '@/services/floor-plan.service';
import { useAuth } from '@/hooks/useAuth';
import type { GymFloorPlan } from '@flash/shared';

/**
 * Props for FloorPlanUpload component
 */
interface FloorPlanUploadProps {
  /** Current floor plan configuration (null if not set) */
  currentFloorPlan: GymFloorPlan | null;
  /** Callback when floor plan changes */
  onFloorPlanChange: (floorPlan: GymFloorPlan | null) => Promise<void>;
  /** Gym ID for storage path */
  gymId: string;
}

/**
 * FloorPlanUpload allows uploading and managing gym floor plans
 */
export const FloorPlanUpload = ({
  currentFloorPlan,
  onFloorPlanChange,
  gymId,
}: FloorPlanUploadProps) => {
  const { user } = useAuth();

  // File selection state
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [success, setSuccess] = useState(false);

  // Deletion state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showReplaceConfirm, setShowReplaceConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Pending file for replace confirmation flow
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  // Map container dimensions for GymMapView
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [mapDimensions, setMapDimensions] = useState({ width: 0, height: 0 });

  // Update map dimensions when container resizes
  useEffect(() => {
    const updateDimensions = () => {
      if (mapContainerRef.current) {
        const { clientWidth } = mapContainerRef.current;
        // Calculate height based on aspect ratio (max 400px)
        const aspectRatio = currentFloorPlan
          ? currentFloorPlan.imageHeight / currentFloorPlan.imageWidth
          : 0.75;
        const height = Math.min(clientWidth * aspectRatio, 400);
        setMapDimensions({ width: clientWidth, height });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [currentFloorPlan]);

  /**
   * Check if current floor plan has regions defined
   */
  const hasRegions = (currentFloorPlan?.regions?.length ?? 0) > 0;
  const regionCount = currentFloorPlan?.regions?.length ?? 0;

  /**
   * Handle file selection from ImageDropZone
   * Auto-uploads immediately unless replacing a floor plan with existing regions
   */
  const handleFileSelect = (file: File) => {
    // Validate the file
    const validation = validateFloorPlanImage(file);
    if (!validation.valid) {
      setError(validation.error ?? 'Invalid file');
      return;
    }

    // If there's an existing floor plan with regions, show replace confirmation
    if (currentFloorPlan && hasRegions) {
      setPendingFile(file);
      setShowReplaceConfirm(true);
      return;
    }

    // Otherwise, proceed with immediate upload
    uploadFile(file);
  };

  /**
   * Upload a file immediately (auto-upload behavior)
   */
  const uploadFile = async (file: File) => {
    if (!user) return;

    // Clear any previous state
    setError(null);
    setSuccess(false);
    setUploading(true);
    setUploadProgress(0);

    // Create preview URL for display during upload
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);

    try {
      // Upload the file and get URL + dimensions
      const result = await uploadFloorPlanImage(file, gymId, user.uid, (progress) =>
        setUploadProgress(progress)
      );

      // Create the new floor plan object
      // If replacing, clear all regions since the image dimensions may have changed
      const newFloorPlan: GymFloorPlan = {
        imageUrl: result.url,
        imageWidth: result.width,
        imageHeight: result.height,
        regions: [], // Always start with empty regions for new/replaced images
      };

      // Save to Firestore
      await onFloorPlanChange(newFloorPlan);

      // Clean up preview
      URL.revokeObjectURL(url);
      setPreviewUrl(null);
      setSuccess(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload floor plan';
      setError(errorMessage);
      console.error('Floor plan upload error:', err);
      // Clean up preview on error
      URL.revokeObjectURL(url);
      setPreviewUrl(null);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  /**
   * Confirm replacement of floor plan with regions - then auto-upload
   */
  const handleConfirmReplace = () => {
    if (pendingFile) {
      uploadFile(pendingFile);
    }
    setPendingFile(null);
    setShowReplaceConfirm(false);
  };

  /**
   * Cancel replacement
   */
  const handleCancelReplace = () => {
    setPendingFile(null);
    setShowReplaceConfirm(false);
  };

  /**
   * Initiate floor plan deletion
   */
  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  /**
   * Confirm and execute floor plan deletion
   */
  const handleConfirmDelete = async () => {
    if (!currentFloorPlan) return;

    setDeleting(true);
    setError(null);
    setSuccess(false);

    try {
      // Attempt to delete from Storage (may fail silently)
      await deleteFloorPlanImage(currentFloorPlan.imageUrl);

      // Clear floor plan from Firestore
      await onFloorPlanChange(null);

      setSuccess(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete floor plan';
      setError(errorMessage);
      console.error('Floor plan deletion error:', err);
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  /**
   * Cancel deletion
   */
  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  /**
   * Handle validation errors from ImageDropZone
   */
  const handleDropZoneError = (message: string) => {
    setError(message);
  };

  // Determine which image to display
  const displayImageUrl = previewUrl ?? currentFloorPlan?.imageUrl ?? null;

  return (
    <div style={containerStyle}>
      {/* Image Display / Upload Zone */}
      {!displayImageUrl ? (
        // No image - show drop zone
        <ImageDropZone
          onFileSelect={handleFileSelect}
          onError={handleDropZoneError}
          maxSize={FLOOR_PLAN_MAX_SIZE}
          allowedTypes={FLOOR_PLAN_ALLOWED_TYPES}
          placeholder="Drag & drop floor plan image or click to browse"
          previewHeight={300}
          disabled={uploading}
          error={error}
        />
      ) : uploading ? (
        // Uploading - show progress overlay on image
        <div style={imageContainerStyle}>
          <img src={displayImageUrl} alt="Floor plan uploading" style={imageStyle} />
          <div style={progressOverlayStyle}>
            <div style={progressBarContainerStyle}>
              <div
                style={{
                  ...progressBarStyle,
                  width: `${uploadProgress}%`,
                }}
              />
            </div>
            <span style={progressTextStyle}>{uploadProgress}%</span>
          </div>
        </div>
      ) : (
        // Current floor plan - show with regions using GymMapView
        <div ref={mapContainerRef} style={mapContainerStyle}>
          {currentFloorPlan && mapDimensions.width > 0 && mapDimensions.height > 0 && (
            <GymMapView
              floorPlan={currentFloorPlan}
              containerWidth={mapDimensions.width}
              containerHeight={mapDimensions.height}
            />
          )}
        </div>
      )}

      {/* Image dimensions info */}
      {currentFloorPlan && !uploading && (
        <p style={dimensionsTextStyle}>
          Image size: {currentFloorPlan.imageWidth} x {currentFloorPlan.imageHeight} pixels
          {hasRegions && ` | ${regionCount} region${regionCount !== 1 ? 's' : ''} defined`}
        </p>
      )}

      {/* Action buttons */}
      <div style={actionsStyle}>
        {currentFloorPlan && !uploading ? (
          // Has existing floor plan - show action buttons
          <>
            <Link to={`/gyms/${gymId}/settings/floor-plan/editor`} style={editRegionsButtonStyle}>
              Edit Regions
            </Link>
            <label style={changeButtonStyle}>
              <input
                type="file"
                accept={FLOOR_PLAN_ALLOWED_TYPES.join(',')}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleFileSelect(file);
                  }
                  e.target.value = ''; // Reset to allow re-selecting same file
                }}
                style={hiddenInputStyle}
                disabled={uploading || deleting}
              />
              Replace Image
            </label>
            <button
              type="button"
              onClick={handleDeleteClick}
              disabled={uploading || deleting}
              style={{
                ...deleteButtonStyle,
                ...(uploading || deleting ? disabledButtonStyle : {}),
              }}
            >
              {deleting ? 'Removing...' : 'Remove Floor Plan'}
            </button>
          </>
        ) : null}
      </div>

      {/* Error message */}
      {error && <div style={errorStyle}>{error}</div>}

      {/* Success message */}
      {success && <div style={successStyle}>Floor plan updated successfully!</div>}

      {/* Help text */}
      <p style={helpTextStyle}>
        JPEG, PNG, WebP, or GIF. Maximum {Math.round(FLOOR_PLAN_MAX_SIZE / (1024 * 1024))}MB. Higher
        resolution images provide better zoom detail.
      </p>

      {/* Replace confirmation dialog */}
      {showReplaceConfirm && (
        <div style={overlayStyle}>
          <div style={dialogStyle}>
            <h3 style={dialogTitleStyle}>Replace Floor Plan?</h3>
            <p style={dialogTextStyle}>
              Replacing the floor plan image will{' '}
              <strong>delete all {regionCount} region(s)</strong> you have drawn.
            </p>
            <p style={warningTextStyle}>
              You will need to redraw all regions on the new image and reassign routes to the new
              regions.
            </p>
            <div style={dialogActionsStyle}>
              <button type="button" onClick={handleCancelReplace} style={dialogCancelButtonStyle}>
                Cancel
              </button>
              <button type="button" onClick={handleConfirmReplace} style={dialogConfirmButtonStyle}>
                Replace Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation dialog */}
      {showDeleteConfirm && (
        <div style={overlayStyle}>
          <div style={dialogStyle}>
            <h3 style={dialogTitleStyle}>Remove Floor Plan?</h3>
            <p style={dialogTextStyle}>
              This will remove the floor plan image
              {hasRegions && <> and all {regionCount} region(s)</>}.
            </p>
            <p style={dialogTextStyle}>
              The map view will become unavailable. Your routes and wall sections will not be
              affected.
            </p>
            <div style={dialogActionsStyle}>
              <button type="button" onClick={handleCancelDelete} style={dialogCancelButtonStyle}>
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={deleting}
                style={{
                  ...dialogDeleteButtonStyle,
                  ...(deleting ? disabledButtonStyle : {}),
                }}
              >
                {deleting ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Component styles using CSS variables for dark mode support
 */
const containerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
};

const imageContainerStyle: CSSProperties = {
  position: 'relative',
  maxWidth: '100%',
  borderRadius: '8px',
  overflow: 'hidden',
  border: '1px solid var(--color-border)',
  backgroundColor: 'var(--color-bg-secondary)',
};

const mapContainerStyle: CSSProperties = {
  width: '100%',
  borderRadius: '8px',
  overflow: 'hidden',
  border: '1px solid var(--color-border)',
  backgroundColor: 'var(--color-bg-secondary)',
};

const imageStyle: CSSProperties = {
  display: 'block',
  maxWidth: '100%',
  maxHeight: '400px',
  objectFit: 'contain',
  margin: '0 auto',
};

const progressOverlayStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
};

const progressBarContainerStyle: CSSProperties = {
  width: '80%',
  maxWidth: '300px',
  height: '8px',
  backgroundColor: 'rgba(255, 255, 255, 0.3)',
  borderRadius: '4px',
  overflow: 'hidden',
};

const progressBarStyle: CSSProperties = {
  height: '100%',
  backgroundColor: 'var(--color-primary)',
  transition: 'width 0.2s',
};

const progressTextStyle: CSSProperties = {
  color: 'white',
  fontSize: '1rem',
  fontWeight: 600,
};

const dimensionsTextStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--color-text-secondary)',
  margin: 0,
};

const actionsStyle: CSSProperties = {
  display: 'flex',
  gap: '12px',
  flexWrap: 'wrap',
};

const editRegionsButtonStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '10px 20px',
  backgroundColor: 'var(--color-success)',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  fontSize: '0.875rem',
  fontWeight: 500,
  cursor: 'pointer',
  textDecoration: 'none',
};

const changeButtonStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '10px 20px',
  backgroundColor: 'var(--color-primary)',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  fontSize: '0.875rem',
  fontWeight: 500,
  cursor: 'pointer',
};

const deleteButtonStyle: CSSProperties = {
  padding: '10px 20px',
  backgroundColor: 'var(--color-badge-warning-bg)',
  color: 'var(--color-error)',
  border: '1px solid var(--color-error)',
  borderRadius: '8px',
  fontSize: '0.875rem',
  fontWeight: 500,
  cursor: 'pointer',
};

const hiddenInputStyle: CSSProperties = {
  display: 'none',
};

const disabledButtonStyle: CSSProperties = {
  opacity: 0.5,
  cursor: 'not-allowed',
};

const errorStyle: CSSProperties = {
  padding: '12px',
  borderRadius: '8px',
  backgroundColor: 'var(--color-badge-warning-bg)',
  border: '1px solid var(--color-error)',
  color: 'var(--color-error)',
  fontSize: '0.875rem',
};

const successStyle: CSSProperties = {
  padding: '12px',
  borderRadius: '8px',
  backgroundColor: 'var(--color-badge-success-bg)',
  border: '1px solid var(--color-success)',
  color: 'var(--color-badge-success-text)',
  fontSize: '0.875rem',
};

const helpTextStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--color-text-muted)',
  margin: 0,
};

const overlayStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '20px',
};

const dialogStyle: CSSProperties = {
  backgroundColor: 'var(--color-bg-primary)',
  borderRadius: '12px',
  padding: '24px',
  maxWidth: '400px',
  width: '100%',
};

const dialogTitleStyle: CSSProperties = {
  fontSize: '1.125rem',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
  marginBottom: '12px',
};

const dialogTextStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-text-secondary)',
  marginBottom: '12px',
};

const warningTextStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-error)',
  backgroundColor: 'var(--color-badge-warning-bg)',
  padding: '12px',
  borderRadius: '6px',
  marginBottom: '16px',
};

const dialogActionsStyle: CSSProperties = {
  display: 'flex',
  gap: '12px',
  justifyContent: 'flex-end',
};

const dialogCancelButtonStyle: CSSProperties = {
  padding: '10px 20px',
  backgroundColor: 'var(--color-bg-tertiary)',
  color: 'var(--color-text-primary)',
  border: 'none',
  borderRadius: '8px',
  fontSize: '0.875rem',
  fontWeight: 500,
  cursor: 'pointer',
};

const dialogConfirmButtonStyle: CSSProperties = {
  padding: '10px 20px',
  backgroundColor: 'var(--color-warning)',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  fontSize: '0.875rem',
  fontWeight: 500,
  cursor: 'pointer',
};

const dialogDeleteButtonStyle: CSSProperties = {
  padding: '10px 20px',
  backgroundColor: 'var(--color-error)',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  fontSize: '0.875rem',
  fontWeight: 500,
  cursor: 'pointer',
};

export default FloorPlanUpload;
